import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Authentication functions
export const authApi = {
    async signIn(email, password) {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });
        if (error) throw error;
        return data;
    },

    async signOut() {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
    },

    async getCurrentUser() {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) throw error;
        return user;
    },

    async getUserProfile(userId) {
        const { data, error } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', userId)
            .single();
        if (error) throw error;
        return data;
    },

    onAuthStateChange(callback) {
        return supabase.auth.onAuthStateChange(callback);
    },

    // Employee login using email and password from employees table
    async signInEmployee(email, password) {
        const { data, error } = await supabase
            .from('employees')
            .select('*')
            .eq('email', email)
            .eq('password_hash', password) // In production, compare with hashed password
            .single();
        
        if (error || !data) {
            throw new Error('Invalid email or password');
        }
        
        // Check if employee is terminated
        const employeeStatus = (data.status || 'active').toLowerCase();
        if (employeeStatus === 'terminated') {
            throw new Error('Your account has been terminated. Please contact HR for assistance.');
        }
        
        // Return employee data in user format
        return {
            id: data.id,
            email: data.email,
            name: data.name,
            role: data.role || 'employee',
            employee_id: data.employee_id,
            department: data.department,
            position: data.position,
            status: data.status || 'active'
        };
    },

    // Admin login using email and password from admins table
    async signInAdmin(email, password) {
        // console.log('🔐 Admin login attempt for:', email);
        
        const { data, error } = await supabase
            .from('admins')
            .select('*')
            .eq('email', email)
            .eq('password', password) // In production, use proper password hashing
            .single();
        
        if (error || !data) {
            console.error('❌ Admin login failed:', error?.message);
            throw new Error('Invalid admin email or password');
        }
        
        // Check if admin account is active (not terminated)
        if (data.is_active === false) {
            console.error('🚫 Admin account is terminated:', data.name);
            throw new Error('Your admin account has been terminated. Please contact the system administrator.');
        }
        
        // console.log('✅ Admin login successful:', data.name);
        
        // Build full name from first_name and last_name if available
        const firstName = data.first_name || data.name?.split(' ')[0] || '';
        const lastName = data.last_name || data.name?.split(' ').slice(1).join(' ') || '';
        const fullName = data.name || `${firstName} ${lastName}`.trim() || 'Admin User';
        
        // console.log('✅ Admin login successful:', fullName);
        
        // Return admin data in user format
        return {
            id: data.id,
            email: data.email,
            name: fullName,
            first_name: firstName,
            last_name: lastName,
            role: data.is_super_admin ? 'super_admin' : (data.role || 'admin'),
            isAdmin: true,
            is_active: data.is_active !== false,
            is_super_admin: data.is_super_admin === true,
            department: data.department || 'Administration',
            position: data.position || (data.is_super_admin ? 'Super Administrator' : 'Administrator'),
            phone: data.phone || '',
            status: data.status || (data.is_active ? 'active' : 'terminated'),
            join_date: data.join_date || data.created_at,
            loginType: 'admin'
        };
    },

    // Change password for employee
    async changeEmployeePassword(employeeId, currentPassword, newPassword) {
        if (!employeeId) {
            throw new Error('Employee ID is required');
        }
        if (!currentPassword || !newPassword) {
            throw new Error('Current and new password are required');
        }
        if (newPassword.length < 6) {
            throw new Error('New password must be at least 6 characters');
        }

        // First verify current password
        const { data: employee, error: fetchError } = await supabase
            .from('employees')
            .select('password_hash')
            .eq('employee_id', employeeId)
            .single();

        if (fetchError || !employee) {
            throw new Error('Employee not found');
        }

        if (employee.password_hash !== currentPassword) {
            throw new Error('Current password is incorrect');
        }

        // Update to new password
        const { error: updateError } = await supabase
            .from('employees')
            .update({ 
                password_hash: newPassword,
                updated_at: new Date().toISOString()
            })
            .eq('employee_id', employeeId);

        if (updateError) {
            console.error('❌ Error updating password:', updateError);
            throw new Error('Failed to update password');
        }

        return { success: true, message: 'Password updated successfully' };
    },

    // Change password for admin
    async changeAdminPassword(adminId, currentPassword, newPassword) {
        if (!adminId) {
            throw new Error('Admin ID is required');
        }
        if (!currentPassword || !newPassword) {
            throw new Error('Current and new password are required');
        }
        if (newPassword.length < 6) {
            throw new Error('New password must be at least 6 characters');
        }

        // First verify current password
        const { data: admin, error: fetchError } = await supabase
            .from('admins')
            .select('password')
            .eq('id', adminId)
            .single();

        if (fetchError || !admin) {
            throw new Error('Admin not found');
        }

        if (admin.password !== currentPassword) {
            throw new Error('Current password is incorrect');
        }

        // Update to new password
        const { error: updateError } = await supabase
            .from('admins')
            .update({ 
                password: newPassword,
                updated_at: new Date().toISOString()
            })
            .eq('id', adminId);

        if (updateError) {
            console.error('❌ Error updating admin password:', updateError);
            throw new Error('Failed to update password');
        }

        return { success: true, message: 'Password updated successfully' };
    }
};

// Timesheet related functions
export const timesheetApi = {
    async createTimesheet(timesheetData) {
        // console.log('📋 Creating timesheet with data:', timesheetData);
        
        // Ensure we have employee_id if not provided 
        if (!timesheetData.employee_id && timesheetData.user) {
            try {
                const employee = await employeeApi.getEmployeeByUser(timesheetData.user);
                timesheetData.employee_id = employee.employee_id || employee.id;
                // Also add employee name for better tracking
                timesheetData.employee_name = employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
                // console.log('✅ Added employee_id to timesheet:', timesheetData.employee_id, 'Name:', timesheetData.employee_name);
            } catch (error) {
                console.error('⚠️ Could not fetch employee_id for timesheet:', error.message);
                // Continue without employee_id but log the issue
            }
        }

        // Remove the user object before inserting to database
        const { user, ...cleanTimesheetData } = timesheetData;

        // Map the data to match the updated schema (no user_id column)
        const timesheetRecord = {
            employee_id: cleanTimesheetData.employee_id,
            date: cleanTimesheetData.workDate || cleanTimesheetData.date,
            hours: parseFloat(cleanTimesheetData.hoursWorked || cleanTimesheetData.hours || 0),
            tasks: JSON.stringify(cleanTimesheetData.tasks || []),
            note: cleanTimesheetData.note || '',
            status: cleanTimesheetData.status || 'pending',
            created_at: new Date().toISOString()
        };

        const { data, error } = await supabase
            .from('timesheets')
            .insert([timesheetRecord])
            .select();
        
        if (error) throw error;
        
        // console.log('✅ Timesheet created successfully:', data[0]);
        return data[0];
    },

    async getTimesheets(employeeId) {
        const { data, error } = await supabase
            .from('timesheets')
            .select('*')
            .eq('employee_id', employeeId)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
    },

    async getTimesheetsByEmployeeId(employeeId) {
        // console.log('🔍 Fetching timesheets for employee:', employeeId);
        
        const { data, error } = await supabase
            .from('timesheets')
            .select(`
                *,
                employees!inner(
                    employee_id,
                    first_name,
                    last_name,
                    name,
                    email
                )
            `)
            .eq('employee_id', employeeId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('❌ Error fetching employee timesheets:', error);
            throw error;
        }

        // console.log('✅ Fetched employee timesheets:', data.length);
        
        // Transform data to include employee name for consistency
        const enrichedData = data.map(timesheet => ({
            ...timesheet,
            employee_name: timesheet.employees?.name || 
                          `${timesheet.employees?.first_name || ''} ${timesheet.employees?.last_name || ''}`.trim() ||
                          `Employee ${timesheet.employee_id}`
        }));

        return enrichedData;
    },

    async updateTimesheet(id, updates) {
        const { data, error } = await supabase
            .from('timesheets')
            .update(updates)
            .eq('id', id)
            .select();

        if (error) throw error;
        return data[0];
    },

    // Admin function: Get ALL employee timesheets with employee names
    async getAllTimesheets() {
        // console.log('🔍 Fetching ALL employee timesheets for admin view...');
        
        const { data, error } = await supabase
            .from('timesheets')
            .select(`
                *,
                employees!inner(
                    employee_id,
                    first_name,
                    last_name,
                    name,
                    email
                )
            `)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('❌ Error fetching all timesheets:', error);
            throw error;
        }

        // console.log('✅ Fetched all timesheets:', data.length);
        
        // Transform data to include employee name
        const enrichedData = data.map(timesheet => ({
            ...timesheet,
            employee_name: timesheet.employees?.name || 
                          `${timesheet.employees?.first_name || ''} ${timesheet.employees?.last_name || ''}`.trim() ||
                          `Employee ${timesheet.employee_id}`
        }));

        return enrichedData;
    }
};

// Leave management related functions
export const leaveApi = {
  async getLeaveBalance(employeeId) {
    console.log('📊 getLeaveBalance called with employeeId:', employeeId);
    
    // STEP 1: Get current year
    const currentYear = new Date().getFullYear();
    console.log('📅 Current year:', currentYear);

    // STEP 2: Query leave_balances with TWO filters
    const { data, error } = await supabase
      .from('leave_balances')
      .select('*')
      .eq('employee_id', employeeId)
      .eq('year', currentYear)
      .maybeSingle();

    console.log('🔍 Query result:', { data, error: error?.message });

    // STEP 3: If no data, return null
    if (!data) {
      console.log('⚠️ No leave balance found for employee', employeeId, 'year', currentYear);
      return null;
    }

    // STEP 4: Extract actual database columns
    const sick_leave_total = data.sick_leave ?? 0;
    const casual_leave_total = data.casual_leave ?? 0;
    const sick_used = data.sick_used ?? 0;
    const casual_used = data.casual_used ?? 0;

    // STEP 5: Calculate remaining values
    const remaining_sick = Math.max(0, sick_leave_total - sick_used);
    const remaining_casual = Math.max(0, casual_leave_total - casual_used);

    console.log('✅ Calculated values:', {
      sick_leave_total,
      casual_leave_total,
      sick_used,
      casual_used,
      remaining_sick,
      remaining_casual
    });

    // STEP 6: Return object with correct fields
    return {
      remaining_sick,
      remaining_casual,
      sick_leave: remaining_sick, // Alias
      casual_leave: remaining_casual, // Alias
      total_sick: sick_leave_total,
      total_casual: casual_leave_total,
      used_sick: sick_used,
      used_casual: casual_used,
      year: data.year,
      employee_id: data.employee_id
    };
  },

  // STEP 7: Add getLeaveBalanceByEmployeeId function (alias)
  async getLeaveBalanceByEmployeeId(employeeId) {
    return leaveApi.getLeaveBalance(employeeId);
  },

  async createLeaveRequest(leaveData) {
  console.log('🏖️ Creating leave request with data:', leaveData);

  // Ensure we have employee_id
  if (!leaveData.employee_id && leaveData.user) {
    const employee = await employeeApi.getEmployeeByUser(leaveData.user);
    leaveData.employee_id = employee.employee_id || employee.id;
  }

  // Remove the user object and document before inserting
  const { user, document, ...cleanLeaveData } = leaveData;

  const leaveRecord = {
    employee_id: cleanLeaveData.employee_id,
    leave_type: cleanLeaveData.leave_type,
    start_date: cleanLeaveData.start_date,
    end_date: cleanLeaveData.end_date,
    reason: cleanLeaveData.reason,
    subject: cleanLeaveData.subject,
    status: cleanLeaveData.status || 'pending',
    created_at: new Date().toISOString(),
  };

  // Handle document upload (keep existing code)
  if (document) {
    try {
      const documentMetadata = await leaveApi.uploadLeaveDocument(document, cleanLeaveData.employee_id);
      if (documentMetadata) {
        leaveRecord.has_documentation = true;
        leaveRecord.document_url = documentMetadata.publicUrl;
        // ... rest of document fields
      }
    } catch (uploadError) {
      console.warn('⚠️ Continuing without document');
    }
  }

  const { data, error } = await supabase
    .from('leave_requests')
    .insert([leaveRecord])
    .select();

  if (error) throw error;
  return data[0];
},




  async updateLeaveBalance(employeeId, leaveType, newBalance) {
    const updateData = {
      [`used_${leaveType}_leaves`]: newBalance,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('leave_balances')
      .update(updateData)
      .eq('employee_id', employeeId);

    if (error) throw error;
  },

  async getLeaveRequests(employeeId) {
    const { data, error } = await supabase
      .from('leave_requests')
      .select('*')
      .eq('employee_id', employeeId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Update leave request status (for admin approve/reject)
  async updateLeaveStatus(leaveId, status, rejectionReason = null) {
    console.log('📝 Updating leave status:', { leaveId, status, rejectionReason });
    
    const updateData = {
      status: status,
      updated_at: new Date().toISOString()
    };
    
    // Add rejection reason if provided
    if (status === 'rejected' && rejectionReason) {
      updateData.rejection_reason = rejectionReason;
    }
    
    const { data, error } = await supabase
      .from('leave_requests')
      .update(updateData)
      .eq('id', leaveId)
      .select();
    
    if (error) {
      console.error('❌ Error updating leave status:', error);
      throw error;
    }
    
    console.log('✅ Leave status updated:', data[0]);
    return data[0];
  },


    // Check if employee has any active approved leave requests for today's date
    async checkActiveLeaveRequest(employeeId, userId) {
        // console.log('🔍 Checking for active leave requests for employee:', employeeId || userId);
        
        const today = new Date().toISOString().slice(0, 10); // Get today's date in YYYY-MM-DD format
        // console.log('📅 Today\'s date:', today);
        
        try {
            const { data, error } = await supabase
                .from('leave_requests')
                .select('*')
                .eq('status', 'approved')
                .lte('start_date', today)
                .gte('end_date', today)
                .eq('employee_id', employeeId || userId);

            if (error) {
                console.error('❌ Error checking active leave requests:', error);
                throw error;
            }

            // console.log('📋 Active leave requests found:', data?.length || 0);
            // if (data && data.length > 0) {
            //     console.log('📝 Active leave details:', data[0]);
            // }
            
            return {
                hasActiveLeave: data && data.length > 0,
                activeLeave: data && data.length > 0 ? data[0] : null
            };
        } catch (error) {
            console.error('❌ Error in checkActiveLeaveRequest:', error);
            throw error;
        }
    },

    // Check if employee has any approved leave requests that overlap a given date range
    async checkOverlappingApprovedLeave(employeeId, userId, startDate, endDate) {
        // console.log('🔍 Checking for overlapping approved leaves for:', employeeId || userId, { startDate, endDate });

        try {
            // Fetch approved leaves for the employee then perform overlap check client-side
            const { data, error } = await supabase
                .from('leave_requests')
                .select('*')
                .eq('status', 'approved')
                .eq('employee_id', employeeId || userId);

            if (error) {
                console.error('❌ Error fetching approved leaves for overlap check:', error);
                throw error;
            }

            // Normalize incoming dates (YYYY-MM-DD)
            const sDate = (startDate || '').split('T')[0];
            const eDate = (endDate || '').split('T')[0];

            const overlapping = (data || []).filter(l => {
                const ls = (l.start_date || '').split('T')[0];
                const le = (l.end_date || '').split('T')[0];
                if (!ls || !le || !sDate || !eDate) return false;

                // Overlap exists unless one range ends before the other starts
                // i.e., NOT (le < sDate OR ls > eDate)
                return !(le < sDate || ls > eDate);
            });

            // console.log('📋 Overlapping approved leaves found:', overlapping.length);
            return { hasOverlap: overlapping.length > 0, overlappingLeaves: overlapping };
        } catch (err) {
            console.error('❌ Error in checkOverlappingApprovedLeave:', err);
            throw err;
        }
    },

    // Admin: Get ALL leave requests with employee info joined
    async getAllLeaveRequestsWithEmployees() {
        // console.log('🔍 Fetching ALL leave requests with employee data for admin view...');
        try {
            // First try with explicit foreign key reference
            const { data, error } = await supabase
                .from('leave_requests')
                .select(`
                    *,
                    employees!leave_requests_employee_id_fkey (
                        id,
                        employee_id,
                        first_name,
                        last_name,
                        name,
                        email,
                        department,
                        position
                    )
                `)
                .order('created_at', { ascending: false });

            if (error) {
                console.warn('⚠️ Join with foreign key failed, trying manual lookup:', error);
                
                // Fallback: Get all leave requests and manually join
                const { data: requests, error: reqError } = await supabase
                    .from('leave_requests')
                    .select('*')
                    .order('created_at', { ascending: false });
                
                if (reqError) throw reqError;
                
                // Get unique employee IDs
                const employeeIds = [...new Set(requests.map(r => r.employee_id).filter(Boolean))];
                // console.log('🆔 Unique employee IDs to lookup:', employeeIds);
                
                // Fetch employees - use employee_id column instead of id
                const { data: employees, error: empError } = await supabase
                    .from('employees')
                    .select('*')
                    .in('employee_id', employeeIds);
                
                if (empError) {
                    console.error('❌ Error fetching employees by employee_id:', empError);
                    
                    // Try alternative: maybe the table uses a different primary key
                    // console.log('🔄 Trying to fetch all employees and match manually...');
                    const { data: allEmployees, error: allEmpError } = await supabase
                        .from('employees')
                        .select('*');
                    
                    if (allEmpError) {
                        console.error('❌ Error fetching all employees:', allEmpError);
                        return requests;
                    }
                    
                    // console.log('👥 Fetched all employees:', allEmployees?.length || 0);
                    // console.log('📋 Sample employee record:', allEmployees?.[0]);
                    
                    // Create lookup map using employee_id
                    const empMap = {};
                    allEmployees?.forEach(emp => {
                        if (emp.employee_id) {
                            empMap[emp.employee_id] = emp;
                        }
                    });
                    
                    // console.log('🗺️ Employee map keys:', Object.keys(empMap));
                    
                    // Merge data
                    const enriched = requests.map(req => ({
                        ...req,
                        employees: empMap[req.employee_id] || null
                    }));
                    
                    // console.log('✅ Manually joined leave requests with employees:', enriched.length);
                    // console.log('📋 Sample enriched request:', enriched[0]);
                    return enriched;
                }
                
                // console.log('👥 Fetched employees:', employees?.length || 0);
                
                // Create lookup map using employee_id
                const empMap = {};
                employees?.forEach(emp => {
                    if (emp.employee_id) {
                        empMap[emp.employee_id] = emp;
                    }
                });
                
                // Merge data
                const enriched = requests.map(req => ({
                    ...req,
                    employees: empMap[req.employee_id] || null
                }));
                
                // console.log('✅ Manually joined leave requests with employees:', enriched.length);
                return enriched;
            }
            
            // console.log('✅ Fetched leave requests with join (admin):', data?.length || 0);
            return data || [];
        } catch (err) {
            console.error('❌ Error fetching all leave requests with employees:', err);
            // Final fallback to simple fetch without join
            const { data: simpleData, error: simpleError } = await supabase
                .from('leave_requests')
                .select('*')
                .order('created_at', { ascending: false });
            if (simpleError) throw simpleError;
            return simpleData || [];
        }
    },

    // Function to get document URL for a leave request
    async getLeaveRequestDocument(leaveRequestId) {
        const { data, error } = await supabase
          .from('leave_requests')
          .select('document_url, document_name')
          .eq('id', leaveRequestId)
          .single();

        if (error) throw error;
        return data;
    },

    async uploadLeaveDocument(file, employeeId) {
        if (!file) return null;

        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${employeeId}-${Date.now()}.${fileExt}`;
            const filePath = fileName; // Don't include folder in path, just the filename

            // console.log('📎 Uploading document:', {
            //     fileName: file.name,
            //     fileSize: file.size,
            //     fileType: file.type,
            //     employeeId,
            //     filePath
            // });

            // Upload file to Supabase Storage
            const { data, error } = await supabase.storage
                .from('leave-documents')
                .upload(filePath, file);

            if (error) {
                console.error('❌ Error uploading document:', error);
                throw error;
            }

            // console.log('✅ Document uploaded successfully:', data);

            // Get public URL for the uploaded file
            const { data: { publicUrl } } = supabase.storage
                .from('leave-documents')
                .getPublicUrl(filePath);

            // console.log('🔗 Generated public URL:', publicUrl);

            // Return comprehensive document metadata
            return {
                path: data.path,
                publicUrl: publicUrl,
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                uploadedAt: new Date().toISOString()
            };
        } catch (error) {
            console.error('❌ Document upload failed:', error);
            throw new Error(`Failed to upload document: ${error.message}`);
        }
    }
};

// Employee management related functions
export const employeeApi = {
    // Bulk fetch map of employee names by both id and employee_id
    async getEmployeeNamesMap(ids) {
        if (!ids || ids.length === 0) return {};
        const unique = [...new Set(ids.map((v) => String(v)))];

        const buildName = (row) =>
            row.name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || `Employee ${String(row.id).slice(-4)}`;

        const map = {};
        try {
            // Query by employee_id
            const { data: byEmpId, error: e1 } = await supabase
                .from('employees')
                .select('id, employee_id, first_name, last_name, name')
                .in('employee_id', unique);
            if (!e1 && byEmpId) {
                for (const row of byEmpId) {
                    if (row.employee_id) map[String(row.employee_id)] = buildName(row);
                }
            }

            // Query by id
            const { data: byId, error: e2 } = await supabase
                .from('employees')
                .select('id, employee_id, first_name, last_name, name')
                .in('id', unique);
            if (!e2 && byId) {
                for (const row of byId) {
                    if (row.id) map[String(row.id)] = buildName(row);
                }
            }
        } catch (err) {
            console.error('❌ getEmployeeNamesMap error:', err);
        }
        return map;
    },
    // Get employee ID by user name, email, or user ID
    async getEmployeeByUser(user) {
        // console.log('🔍 Looking up employee for user:', user);
        
        if (!user) {
            throw new Error('User data is required');
        }

        // Try multiple lookup strategies in order of preference
        let employee = null;
        let error = null;

        // Strategy 1: If user already has employee_id, use it directly
        if (user.employee_id) {
            try {
                const { data, error: empError } = await supabase
                    .from('employees')
                    .select('*')
                    .eq('employee_id', user.employee_id)
                    .single();
                
                if (!empError && data) {
                    // console.log('✅ Found employee by employee_id:', data);
                    return data;
                }
            } catch (e) {
                console.error('⚠️ Employee lookup by employee_id failed:', e.message);
            }
        }

        // Strategy 2: Try email lookup
        if (user.email) {
            try {
                const { data, error: emailError } = await supabase
                    .from('employees')
                    .select('*')
                    .eq('email', user.email)
                    .single();
                
                if (!emailError && data) {
                    // console.log('✅ Found employee by email:', data);
                    return data;
                }
            } catch (e) {
                console.error('⚠️ Employee lookup by email failed:', e.message);
            }
        }

        // Strategy 3: Try exact name match
        if (user.name) {
            try {
                const { data, error: nameError } = await supabase
                    .from('employees')
                    .select('*')
                    .eq('name', user.name)
                    .single();
                
                if (!nameError && data) {
                    // console.log('✅ Found employee by exact name:', data);
                    return data;
                }
            } catch (e) {
                console.error('⚠️ Employee lookup by exact name failed:', e.message);
            }
        }

        // Strategy 4: Try partial name match
        if (user.name) {
            try {
                const nameParts = user.name.split(' ');
                const firstName = nameParts[0];
                const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

                let nameQuery = supabase.from('employees').select('*');
                
                if (firstName && lastName) {
                    // Try first + last name combination
                    nameQuery = nameQuery.or(`name.ilike.%${user.name}%,first_name.ilike.%${firstName}%,last_name.ilike.%${lastName}%`);
                } else {
                    // Try just the name
                    nameQuery = nameQuery.or(`name.ilike.%${user.name}%,first_name.ilike.%${user.name}%,last_name.ilike.%${user.name}%`);
                }

                const { data, error: partialError } = await nameQuery.limit(1).single();
                
                if (!partialError && data) {
                    // console.log('✅ Found employee by partial name match:', data);
                    return data;
                }
            } catch (e) {
                console.error('⚠️ Employee lookup by partial name failed:', e.message);
                error = e;
            }
        }

        // If all strategies failed
        console.error('❌ All employee lookup strategies failed for user:', user);
        throw new Error(`Employee not found in database. Please ensure the employee record exists with email: ${user.email} or name: ${user.name}`);
    },

    async createEmployee(employeeData) {
        // console.log('👤 Creating employee with data:', employeeData);
        
        // Process the employee data
        const processedData = {
            ...employeeData,
            // For demo purposes, storing password as-is. In production, hash it!
            password_hash: employeeData.password,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
        };
        
        // Remove the plain password field
        delete processedData.password;
        
        // Ensure first_name and last_name are properly set
        // if (processedData.first_name && processedData.last_name) {
        //     console.log('✅ Employee has first_name and last_name:', {
        //         first_name: processedData.first_name,
        //         last_name: processedData.last_name
        //     });
        // } else {
        //     console.log('⚠️ Employee missing first_name or last_name:', {
        //         first_name: processedData.first_name,
        //         last_name: processedData.last_name
        //     });
        // }
        
        const { data, error } = await supabase
            .from('employees')
            .insert([processedData])
            .select();
        
        if (error) {
            console.error('❌ Error creating employee:', error);
            throw error;
        }
        
        // console.log('✅ Employee created successfully:', data[0]);
        return data[0];
    },

    // Create a new admin user with all fields
    async createAdmin(adminData) {
        console.log('👤 Creating admin with data:', adminData);
        
        // Process the admin data with all new fields
        const processedData = {
            name: adminData.name,
            email: adminData.email,
            password: adminData.password, // For demo purposes, storing as-is. In production, hash it!
            role: adminData.role || 'Admin',
            first_name: adminData.first_name || adminData.name?.split(' ')[0] || '',
            last_name: adminData.last_name || adminData.name?.split(' ').slice(1).join(' ') || '',
            phone: adminData.phone || '',
            department: adminData.department || 'Administration',
            position: adminData.position || 'Administrator',
            join_date: adminData.join_date || new Date().toISOString().split('T')[0],
            is_active: adminData.is_active !== false, // Default to true
            status: adminData.status || 'Active',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
        };
        
        const { data, error } = await supabase
            .from('admins')
            .insert([processedData])
            .select();
        
        if (error) {
            console.error('❌ Error creating admin:', error);
            throw error;
        }
        
        console.log('✅ Admin created successfully:', data[0]);
        return data[0];
    },

    // Update an existing admin
    async updateAdmin(adminId, updates, currentUserRole = null, currentUserId = null, currentUserIsSuperAdmin = false) {
        console.log('🔄 Updating admin:', adminId, updates, 'Current user role:', currentUserRole, 'Current user ID:', currentUserId, 'Is Super Admin:', currentUserIsSuperAdmin);
        
        if (!adminId) {
            throw new Error('Admin ID is required for update');
        }
        
        // Check if admin is editing their own account
        const isEditingSelf = currentUserId === adminId;
        
        // Check if current user is Super Admin (using boolean or legacy role)
        const isCurrentUserSuperAdmin = currentUserIsSuperAdmin === true || currentUserRole === 'super_admin';
        
        // Security check: Allow if Super Admin OR editing own account
        if (!isCurrentUserSuperAdmin && !isEditingSelf) {
            console.error('❌ Access denied: Only Super Admin can update other admin accounts');
            throw new Error("You don't have access to edit these accounts.");
        }
        
        // First, check if the target admin is a Super Admin
        const { data: targetAdmin, error: fetchError } = await supabase
            .from('admins')
            .select('role, email, is_super_admin')
            .eq('id', adminId)
            .single();
        
        if (fetchError) {
            console.error('❌ Error fetching target admin:', fetchError);
            throw fetchError;
        }
        
        // Additional security: Cannot edit Super Admin unless you're Super Admin
        const isTargetSuperAdmin = targetAdmin?.is_super_admin === true || targetAdmin?.role === 'super_admin';
        if (isTargetSuperAdmin && !isCurrentUserSuperAdmin) {
            console.error('❌ Access denied: Cannot edit Super Admin account');
            throw new Error("You don't have access to edit these accounts.");
        }
        
        // Process updates
        const processedUpdates = {
            ...updates,
            updated_at: new Date().toISOString()
        };
        
        // If first_name or last_name is being updated, sync the name field
        if (updates.first_name !== undefined || updates.last_name !== undefined) {
            const firstName = updates.first_name !== undefined ? updates.first_name : targetAdmin?.first_name;
            const lastName = updates.last_name !== undefined ? updates.last_name : targetAdmin?.last_name;
            processedUpdates.name = `${firstName || ''} ${lastName || ''}`.trim();
            console.log('📝 Syncing admin name field:', processedUpdates.name);
        }
        
        // If is_active is being updated, also update status
        if (updates.is_active !== undefined) {
            processedUpdates.status = updates.is_active ? 'Active' : 'Terminated';
            if (!updates.is_active) {
                processedUpdates.terminated_at = new Date().toISOString();
            }
        }
        
        const { data, error } = await supabase
            .from('admins')
            .update(processedUpdates)
            .eq('id', adminId)
            .select();
        
        if (error) {
            console.error('❌ Error updating admin:', error);
            throw error;
        }
        
        console.log('✅ Admin updated successfully:', data[0]);
        return data[0];
    },

    // Terminate an admin by setting is_active to false
    async terminateAdmin(adminId, currentUserRole = null, currentUserIsSuperAdmin = false) {
        console.log('🚫 Terminating admin:', adminId, 'Current user role:', currentUserRole, 'Is Super Admin:', currentUserIsSuperAdmin);
        
        if (!adminId) {
            throw new Error('Admin ID is required for termination');
        }
        
        // Security check: Only Super Admin can terminate admin accounts
        const isCurrentUserSuperAdmin = currentUserIsSuperAdmin === true || currentUserRole === 'super_admin';
        if (!isCurrentUserSuperAdmin) {
            console.error('❌ Access denied: Only Super Admin can terminate admin accounts');
            throw new Error("You don't have access to terminate these accounts.");
        }
        
        // First, check if the target admin is a Super Admin
        const { data: targetAdmin, error: fetchError } = await supabase
            .from('admins')
            .select('role, email, is_super_admin')
            .eq('id', adminId)
            .single();
        
        if (fetchError) {
            console.error('❌ Error fetching target admin:', fetchError);
            throw fetchError;
        }
        
        // Cannot terminate Super Admin
        const isTargetSuperAdmin = targetAdmin?.is_super_admin === true || targetAdmin?.role === 'super_admin';
        if (isTargetSuperAdmin) {
            console.error('❌ Access denied: Super Admin account cannot be terminated');
            throw new Error('The Super Admin account cannot be terminated.');
        }
        
        const updates = {
            is_active: false,
            status: 'Terminated',
            terminated_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
        };
        
        const { data, error } = await supabase
            .from('admins')
            .update(updates)
            .eq('id', adminId)
            .select();
        
        if (error) {
            console.error('❌ Error terminating admin:', error);
            throw error;
        }
        
        console.log('✅ Admin terminated successfully:', data[0]);
        return data[0];
    },

    async getEmployees() {
        // console.log('📋 Fetching employees from database...');
        
        try {
            const { data, error } = await supabase
                .from('employees')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) {
                console.error('❌ Database error in getEmployees:', error);
                throw error;
            }
            
            // console.log('✅ Successfully fetched employees:', data?.length || 0);
            if (data && data.length > 0) {
                // console.log('📋 Sample employee record:', {
                //     id: data[0].id,
                //     employee_id: data[0].employee_id,
                //     name: data[0].name,
                //     email: data[0].email
                // });
            }
            
            return data || [];
        } catch (err) {
            console.error('❌ Exception in getEmployees:', err);
            throw err;
        }
    },

    async updateEmployee(employeeId, updates) {
        // console.log('🔄 Updating employee:', { employeeId, updates });
        
        if (!employeeId) {
            throw new Error('Employee ID is required for updating employee');
        }
        
        const { data, error } = await supabase
            .from('employees')
            .update({
                ...updates,
                updated_at: new Date().toISOString()
            })
            .eq('employee_id', employeeId)
            .select();

        if (error) {
            console.error('❌ Error updating employee:', error);
            throw error;
        }
        
        // console.log('✅ Employee updated successfully:', data[0]);
        return data[0];
    },

    async deleteEmployee(employeeId) {
        const { error } = await supabase
            .from('employees')
            .delete()
            .eq('employee_id', employeeId);

        if (error) throw error;
        return true;
    },

    // Update only allowed profile fields (user-editable fields)
    async updateEmployeeProfile(employeeId, profileData) {
        if (!employeeId) {
            throw new Error('Employee ID is required for updating profile');
        }

        // Only allow these fields to be updated by the user
        const allowedFields = ['first_name', 'last_name', 'name', 'phone', 'email'];
        const sanitizedUpdates = {};
        
        for (const field of allowedFields) {
            if (profileData[field] !== undefined) {
                sanitizedUpdates[field] = profileData[field];
            }
        }

        // Auto-generate name from first_name and last_name if provided
        if (sanitizedUpdates.first_name || sanitizedUpdates.last_name) {
            const firstName = sanitizedUpdates.first_name || profileData.first_name || '';
            const lastName = sanitizedUpdates.last_name || profileData.last_name || '';
            sanitizedUpdates.name = `${firstName} ${lastName}`.trim();
        }

        sanitizedUpdates.updated_at = new Date().toISOString();

        const { data, error } = await supabase
            .from('employees')
            .update(sanitizedUpdates)
            .eq('employee_id', employeeId)
            .select();

        if (error) {
            console.error('❌ Error updating employee profile:', error);
            throw error;
        }
        
        return data[0];
    },

    // Get full employee profile by employee_id
    async getEmployeeProfile(employeeId) {
        const { data, error } = await supabase
            .from('employees')
            .select('*')
            .eq('employee_id', employeeId)
            .single();

        if (error) {
            console.error('❌ Error fetching employee profile:', error);
            throw error;
        }
        
        return data;
    },

    // Get admin profile by admin id
    async getAdminProfile(adminId) {
        console.log('🔍 getAdminProfile called with adminId:', adminId);
        
        if (!adminId) {
            throw new Error('Admin ID is required');
        }
        
        // Try fetching by id first
        let { data, error } = await supabase
            .from('admins')
            .select('*')
            .eq('id', adminId)
            .single();

        // If not found by id, try admin_id field
        if (error || !data) {
            console.log('⚠️ Admin not found by id, trying admin_id field...');
            const result = await supabase
                .from('admins')
                .select('*')
                .eq('admin_id', adminId)
                .single();
            data = result.data;
            error = result.error;
        }

        if (error) {
            console.error('❌ Error fetching admin profile:', error);
            throw error;
        }
        
        if (!data) {
            console.error('❌ No admin found with ID:', adminId);
            throw new Error('Admin not found');
        }
        
        console.log('✅ Admin data fetched:', data);
        
        // Build full name from first_name and last_name if available, otherwise use name field
        const firstName = data.first_name || data.name?.split(' ')[0] || '';
        const lastName = data.last_name || data.name?.split(' ').slice(1).join(' ') || '';
        const fullName = data.name || `${firstName} ${lastName}`.trim() || 'Admin User';
        
        // Transform admin data to match employee profile structure
        const transformed = {
            id: data.id,
            employee_id: data.admin_id || data.id, // Use admin_id if available, otherwise id
            name: fullName,
            first_name: firstName,
            last_name: lastName,
            email: data.email,
            phone: data.phone || '',
            department: data.department || 'Administration',
            position: data.position || (data.is_super_admin ? 'Super Administrator' : 'Administrator'),
            role: data.is_super_admin ? 'super_admin' : (data.role || 'admin'),
            status: data.status || (data.is_active ? 'active' : 'terminated'),
            is_active: data.is_active !== false, // Default to true if not set
            is_super_admin: data.is_super_admin === true,
            join_date: data.join_date || data.created_at,
            created_at: data.created_at,
            updated_at: data.updated_at,
            terminated_at: data.terminated_at,
            isAdmin: true
        };
        
        console.log('✅ Transformed admin profile:', transformed);
        return transformed;
    },

    // Update admin profile - only allow personal info updates
    async updateAdminProfile(adminId, profileData) {
        if (!adminId) {
            throw new Error('Admin ID is required for updating profile');
        }

        // Only allow these fields to be updated by the user
        const allowedFields = ['name', 'first_name', 'last_name', 'phone'];
        const sanitizedUpdates = {};
        
        for (const field of allowedFields) {
            if (profileData[field] !== undefined) {
                sanitizedUpdates[field] = profileData[field];
            }
        }

        // If first_name or last_name provided, update name field for backward compatibility
        if (profileData.first_name || profileData.last_name) {
            const firstName = profileData.first_name || '';
            const lastName = profileData.last_name || '';
            sanitizedUpdates.name = `${firstName} ${lastName}`.trim();
        }
        
        // If name provided but not first_name/last_name, derive them
        if (profileData.name && !profileData.first_name && !profileData.last_name) {
            const nameParts = profileData.name.split(' ');
            sanitizedUpdates.first_name = nameParts[0] || '';
            sanitizedUpdates.last_name = nameParts.slice(1).join(' ') || '';
        }

        sanitizedUpdates.updated_at = new Date().toISOString();

        const { data, error } = await supabase
            .from('admins')
            .update(sanitizedUpdates)
            .eq('id', adminId)
            .select();

        if (error) {
            console.error('❌ Error updating admin profile:', error);
            throw error;
        }

        return data[0];
    }
};

// Holidays API functions
export const holidayApi = {
    // Get all holidays (optionally filter by year)
    async getHolidays(year = null) {
        console.log('📅 Fetching holidays from database...', year ? `for year ${year}` : 'all');
        
        let query = supabase
            .from('holidays')
            .select('*')
            .order('date', { ascending: true });
        
        if (year) {
            // Filter by year - dates are stored as YYYY-MM-DD
            query = query
                .gte('date', `${year}-01-01`)
                .lte('date', `${year}-12-31`);
        }
        
        const { data, error } = await query;
        
        if (error) {
            console.error('❌ Error fetching holidays:', error);
            throw error;
        }
        
        console.log(`✅ Fetched ${data?.length || 0} holidays`);
        return data || [];
    },

    // Upload holidays from Excel file data
    async uploadHolidays(holidays) {
        console.log('📤 Uploading holidays to database...', holidays.length, 'records');
        
        if (!holidays || holidays.length === 0) {
            throw new Error('No holidays to upload');
        }

        // Group holidays by year
        const holidaysByYear = {};
        holidays.forEach(h => {
            const year = h.date.split('-')[0];
            if (!holidaysByYear[year]) {
                holidaysByYear[year] = [];
            }
            holidaysByYear[year].push(h);
        });

        console.log('📊 Holidays grouped by year:', Object.keys(holidaysByYear));

        // For each year, delete existing holidays first, then insert new ones
        for (const year of Object.keys(holidaysByYear)) {
            console.log(`🗑️ Deleting existing holidays for year ${year}...`);
            
            const { error: deleteError } = await supabase
                .from('holidays')
                .delete()
                .gte('date', `${year}-01-01`)
                .lte('date', `${year}-12-31`);

            if (deleteError) {
                console.error(`❌ Error deleting holidays for ${year}:`, deleteError);
                throw deleteError;
            }
        }

        // Prepare records for insertion
        const records = holidays.map(h => ({
            date: h.date,
            name: h.name || 'Holiday',
            created_at: new Date().toISOString()
        }));

        // Insert new holidays
        const { data, error } = await supabase
            .from('holidays')
            .insert(records)
            .select();

        if (error) {
            console.error('❌ Error inserting holidays:', error);
            throw error;
        }

        console.log(`✅ Successfully uploaded ${data?.length || 0} holidays`);
        return data;
    },

    // Add a single holiday
    async addHoliday(date, name) {
        console.log('➕ Adding holiday:', date, name);
        
        const { data, error } = await supabase
            .from('holidays')
            .insert({
                date,
                name: name || 'Holiday',
                created_at: new Date().toISOString()
            })
            .select()
            .single();

        if (error) {
            console.error('❌ Error adding holiday:', error);
            throw error;
        }

        return data;
    },

    // Delete a holiday by ID
    async deleteHoliday(id) {
        console.log('🗑️ Deleting holiday:', id);
        
        const { error } = await supabase
            .from('holidays')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('❌ Error deleting holiday:', error);
            throw error;
        }

        return { success: true };
    },

    // Delete all holidays for a specific year
    async deleteHolidaysByYear(year) {
        console.log('🗑️ Deleting all holidays for year:', year);
        
        const { error } = await supabase
            .from('holidays')
            .delete()
            .gte('date', `${year}-01-01`)
            .lte('date', `${year}-12-31`);

        if (error) {
            console.error('❌ Error deleting holidays:', error);
            throw error;
        }

        return { success: true };
    }
};

// Timesheet Compliance API - Warning & Auto Leave Deduction
export const timesheetComplianceApi = {
    
     
    formatDateLocal(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    },

    /**
     * Check if a date is covered by an approved or pending leave request
     * @param {string} dateStr - Date to check in YYYY-MM-DD format
     * @param {Array} leaveRequests - Array of leave request objects
     * @returns {Object} - { isOnLeave: boolean, leaveDetails: object|null }
     */
    isDateCoveredByLeave(dateStr, leaveRequests) {
        const checkDate = new Date(dateStr);
        checkDate.setHours(0, 0, 0, 0);
        
        for (const leave of leaveRequests || []) {
            const startDate = new Date(leave.start_date);
            const endDate = new Date(leave.end_date);
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);
            
            // Check if date falls within leave period
            if (checkDate >= startDate && checkDate <= endDate) {
                const status = (leave.status || '').toLowerCase();
                // Consider both approved and pending leaves
                if (status === 'approved' || status === 'pending') {
                    return {
                        isOnLeave: true,
                        leaveDetails: {
                            type: leave.leave_type,
                            status: leave.status,
                            startDate: leave.start_date,
                            endDate: leave.end_date
                        }
                    };
                }
            }
        }
        
        return { isOnLeave: false, leaveDetails: null };
    },

    /**
     * Check for missing timesheets and return warnings/auto-leave info
     * Day+1 = Warning, Day+2 = Auto Leave Deduction
     * Excludes weekends (Sundays) and holidays
     * Only checks current year - fresh start each year
     * Validates: employee creation date, approved/pending leaves, holidays, weekends, leave balance
     */
    async checkMissingTimesheets(employeeId) {
        console.log('🔍 Checking missing timesheets for employee:', employeeId);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const currentYear = today.getFullYear();
        const todayStr = this.formatDateLocal(today);
        
        console.log(`📅 Today is: ${todayStr} (year: ${currentYear})`);
        
        const result = {
            warnings: [],        // Days that need warning (Day+1)
            autoLeaveRequired: [], // Days that need auto-leave (Day+2)
            processed: [],       // Already processed auto-leaves
            skipped: []          // Days skipped due to validation
        };
        
        try {
            // Get employee details including created_at date
            const { data: employee, error: empError } = await supabase
                .from('employees')
                .select('id, created_at, name')
                .eq('id', employeeId)
                .single();
            
            if (empError) {
                console.warn('⚠️ Skipping compliance check for employee:', employeeId, '- Error:', empError.message);
                // Return empty result instead of throwing to prevent cascading errors
                return {
                    warnings: [],
                    autoLeaveRequired: [],
                    processed: [],
                    skipped: [{ employeeId, reason: 'Employee not accessible or RLS policy blocking' }]
                };
            }
            
            if (!employee) {
                console.warn('⚠️ Employee not found:', employeeId);
                return {
                    warnings: [],
                    autoLeaveRequired: [],
                    processed: [],
                    skipped: [{ employeeId, reason: 'Employee record not found' }]
                };
            }
            
            const employeeCreatedAt = new Date(employee.created_at);
            employeeCreatedAt.setHours(0, 0, 0, 0);
            console.log(`👤 Employee created at: ${this.formatDateLocal(employeeCreatedAt)}`);
            
            // Get holidays for the current year
            const holidays = await holidayApi.getHolidays(currentYear);
            const holidayDates = new Set(holidays.map(h => h.date));
            
            // Get approved and pending leave requests for the past week only
            // This covers the check period (yesterday and potential Day+2 auto-leave dates)
            const oneWeekAgo = new Date(today);
            oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
            const oneWeekAgoStr = this.formatDateLocal(oneWeekAgo);
            
            const { data: leaveRequests, error: leaveError } = await supabase
                .from('leave_requests')
                .select('start_date, end_date, status, leave_type, reason')
                .eq('employee_id', employeeId)
                .gte('start_date', oneWeekAgoStr)
                .lte('end_date', todayStr)
                .or('status.eq.approved,status.eq.pending');
            
            if (leaveError) {
                console.error('❌ Error fetching leave requests:', leaveError);
                throw leaveError;
            }
            
            console.log(`📋 Found ${leaveRequests?.length || 0} approved/pending leave requests (past 7 days)`);
            
            // Get current leave balance
            const leaveBalance = await leaveApi.getLeaveBalance(employeeId);
            const totalRemainingLeaves = (leaveBalance?.remaining_casual || 0) + (leaveBalance?.remaining_sick || 0);
            console.log(`💼 Leave balance - Casual: ${leaveBalance?.remaining_casual || 0}, Sick: ${leaveBalance?.remaining_sick || 0}, Total: ${totalRemainingLeaves}`);
            
            // Use employee's account creation date as the starting point for compliance checks
            // This ensures new employees are not penalized for dates before they joined
            const startDate = employeeCreatedAt;
            const startDateStr = this.formatDateLocal(startDate);
            
            console.log(`📅 Checking timesheets from ${startDateStr} (employee account creation date)`);
            
            const { data: timesheets, error: tsError } = await supabase
                .from('timesheets')
                .select('date')
                .eq('employee_id', employeeId)
                .gte('date', startDateStr);
            
            if (tsError) throw tsError;
            
            // Create set of dates with timesheets
            const timesheetDates = new Set(
                (timesheets || []).map(t => t.date?.split('T')[0])
            );
            
            console.log('📋 Timesheets found:', Array.from(timesheetDates));
            
            // Get existing auto-leave records to avoid duplicates
            // Use broader check - any leave for a single day in this period
            const { data: existingAutoLeaves, error: alError } = await supabase
                .from('leave_requests')
                .select('start_date, end_date, reason')
                .eq('employee_id', employeeId)
                .gte('start_date', startDateStr);
            
            if (alError) throw alError;
            
            // Create set of dates that already have auto-leaves (broader matching)
            const autoLeaveDates = new Set();
            (existingAutoLeaves || []).forEach(leave => {
                const startDate = leave.start_date?.split('T')[0];
                const endDate = leave.end_date?.split('T')[0];
                const reason = (leave.reason || '').toLowerCase();
                
                // Only consider single-day leaves with timesheet-related reasons as auto-leaves
                if (startDate === endDate && 
                    (reason.includes('timesheet') || reason.includes('auto'))) {
                    autoLeaveDates.add(startDate);
                }
            });
            
            console.log('📋 Existing auto-leave dates:', Array.from(autoLeaveDates));
            
            // Check each day from startDate until yesterday
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            const yesterdayStr = this.formatDateLocal(yesterday);
            
            console.log(`📆 Looping from ${startDateStr} to ${yesterdayStr}`);
            
            for (let d = new Date(startDate); d <= yesterday; d.setDate(d.getDate() + 1)) {
                // Use local date format to avoid timezone issues
                const dateStr = this.formatDateLocal(d);
                const dayOfWeek = d.getDay();
                
                // VALIDATION 1: Skip Sundays (0 = Sunday)
                if (dayOfWeek === 0) {
                    console.log(`⏭️ Skipping ${dateStr} - Sunday (weekend)`);
                    result.skipped.push({ date: dateStr, reason: 'Sunday (weekend)' });
                    continue;
                }
                
                // VALIDATION 2: Skip holidays
                if (holidayDates.has(dateStr)) {
                    console.log(`⏭️ Skipping ${dateStr} - Holiday`);
                    result.skipped.push({ date: dateStr, reason: 'Holiday' });
                    continue;
                }
                
                // VALIDATION 3: Skip if employee is on approved or pending leave (sick, casual, or any leave type)
                const leaveCheck = this.isDateCoveredByLeave(dateStr, leaveRequests);
                if (leaveCheck.isOnLeave) {
                    console.log(`⏭️ Skipping ${dateStr} - Employee on ${leaveCheck.leaveDetails.status} ${leaveCheck.leaveDetails.type} leave`);
                    result.skipped.push({ 
                        date: dateStr, 
                        reason: `On ${leaveCheck.leaveDetails.status} ${leaveCheck.leaveDetails.type} leave` 
                    });
                    continue;
                }
                
                // Check if timesheet exists for this date
                if (timesheetDates.has(dateStr)) {
                    continue; // Timesheet submitted, all good
                }
                
                // Calculate days since this missing date
                const daysDiff = Math.floor((today - d) / (1000 * 60 * 60 * 24));
                
                console.log(`❓ Missing timesheet for ${dateStr}, daysDiff: ${daysDiff}`);
                
                // VALIDATION 4: Check if employee has sufficient leave balance before auto-deducting
                if (daysDiff >= 2 && totalRemainingLeaves <= 0) {
                    console.log(`⏭️ Skipping ${dateStr} - Insufficient leave balance (Casual: ${leaveBalance?.remaining_casual || 0}, Sick: ${leaveBalance?.remaining_sick || 0})`);
                    result.skipped.push({ 
                        date: dateStr, 
                        reason: 'Insufficient leave balance for auto-deduction' 
                    });
                    // Still add to warnings so employee knows they need to submit
                    result.warnings.push({
                        date: dateStr,
                        message: `Timesheet for ${dateStr} is not submitted. Please submit immediately - no leave balance available for auto-deduction.`
                    });
                    continue;
                }
                
                if (autoLeaveDates.has(dateStr)) {
                    // Already processed as auto-leave
                    result.processed.push({
                        date: dateStr,
                        message: `Auto-leave already applied for ${dateStr}`
                    });
                } else if (daysDiff >= 2) {
                    // Day+2 or more: Needs auto-leave deduction
                    result.autoLeaveRequired.push({
                        date: dateStr,
                        daysDiff,
                        message: `Timesheet for ${dateStr} was not submitted. Leave will be deducted.`
                    });
                } else if (daysDiff === 1) {
                    // Day+1: Warning only
                    result.warnings.push({
                        date: dateStr,
                        message: `Timesheet for ${dateStr} is not submitted. Please submit today to avoid leave deduction.`
                    });
                }
            }
            
            console.log('📋 Missing timesheet check result:', result);
            return result;
            
        } catch (error) {
            console.error('❌ Error checking missing timesheets:', error);
            throw error;
        }
    },

    /**
     * Create auto-leave record for missing timesheet
     * Deducts from casual leave first, then sick leave
     */
    async createAutoLeaveForMissingTimesheet(employeeId, missingDate) {
        console.log('⚠️ Creating auto-leave for missing timesheet:', employeeId, missingDate);
        
        // Generate a unique key for this operation to prevent race conditions
        const operationKey = `auto-leave-${employeeId}-${missingDate}`;
        
        // Check if this operation is already in progress (race condition prevention)
        if (this._processingLeaves && this._processingLeaves.has(operationKey)) {
            console.log('⏭️ Auto-leave creation already in progress for:', operationKey);
            return { skipped: true, reason: 'already_processing' };
        }
        
        // Initialize processing set if not exists
        if (!this._processingLeaves) {
            this._processingLeaves = new Set();
        }
        this._processingLeaves.add(operationKey);
        
        try {
            // CRITICAL: Check if ANY leave already exists for this date (broader check)
            // This catches auto-leaves regardless of exact reason text
            const { data: existingLeaves, error: checkError } = await supabase
                .from('leave_requests')
                .select('id, reason, status')
                .eq('employee_id', employeeId)
                .eq('start_date', missingDate)
                .eq('end_date', missingDate);
            
            if (checkError) {
                console.error('⚠️ Error checking existing auto-leave:', checkError);
            }
            
            // Check if any auto-leave (with timesheet-related reason) exists
            const autoLeaveExists = existingLeaves?.some(leave => 
                leave.reason?.toLowerCase().includes('timesheet') ||
                leave.reason?.toLowerCase().includes('auto')
            );
            
            if (autoLeaveExists) {
                console.log('⏭️ Auto-leave already exists for this date, skipping:', missingDate);
                this._processingLeaves.delete(operationKey);
                return existingLeaves[0]; // Return existing record instead of creating duplicate
            }
            
            // Get current leave balance
            const leaveBalance = await leaveApi.getLeaveBalance(employeeId);
            
            if (!leaveBalance) {
                console.warn('⚠️ No leave balance found for employee:', employeeId);
                // Still create the record but mark as unpaid/no balance
            }
            
            // Determine which leave type to deduct (casual first, then sick)
            let leaveType = 'casual';
            if (leaveBalance && leaveBalance.remaining_casual <= 0) {
                leaveType = 'sick';
            }
            
            // Create auto-leave request
            const leaveRecord = {
                employee_id: employeeId,
                leave_type: leaveType,
                start_date: missingDate,
                end_date: missingDate,
                reason: 'Timesheet not submitted on this day',
                subject: 'Auto Leave - Missing Timesheet',
                status: 'approved', // Auto-approved since it's a system action
                created_at: new Date().toISOString()
            };
            
            const { data, error } = await supabase
                .from('leave_requests')
                .insert([leaveRecord])
                .select();
            
            if (error) {
                // Check if it's a duplicate key error (race condition at DB level)
                if (error.code === '23505' || error.message?.includes('duplicate')) {
                    console.log('⏭️ Duplicate detected at DB level, skipping:', missingDate);
                    this._processingLeaves.delete(operationKey);
                    return { skipped: true, reason: 'duplicate_at_db' };
                }
                throw error;
            }
            
            // Update leave balance - increment used count
            const usedColumn = leaveType === 'casual' ? 'casual_used' : 'sick_used';
            const currentUsed = leaveType === 'casual' 
                ? (leaveBalance?.used_casual || 0) 
                : (leaveBalance?.used_sick || 0);
            
            const { error: updateError } = await supabase
                .from('leave_balances')
                .update({ 
                    [usedColumn]: currentUsed + 1,
                    updated_at: new Date().toISOString()
                })
                .eq('employee_id', employeeId)
                .eq('year', new Date().getFullYear());
            
            if (updateError) {
                console.error('⚠️ Error updating leave balance:', updateError);
                // Don't throw - the leave request was created successfully
            }
            
            console.log('✅ Auto-leave created successfully:', data[0]);
            this._processingLeaves.delete(operationKey);
            return data[0];
            
        } catch (error) {
            console.error('❌ Error creating auto-leave:', error);
            this._processingLeaves.delete(operationKey);
            throw error;
        }
    },

    /**
     * Process all pending auto-leaves for an employee
     * Called when employee logs in or dashboard loads
     */
    async processAutoLeaves(employeeId) {
        console.log('🔄 Processing auto-leaves for employee:', employeeId);
        
        const checkResult = await this.checkMissingTimesheets(employeeId);
        const processedLeaves = [];
        
        for (const missing of checkResult.autoLeaveRequired) {
            try {
                const leave = await this.createAutoLeaveForMissingTimesheet(employeeId, missing.date);
                processedLeaves.push({
                    date: missing.date,
                    success: true,
                    leave
                });
            } catch (error) {
                processedLeaves.push({
                    date: missing.date,
                    success: false,
                    error: error.message
                });
            }
        }
        
        return {
            warnings: checkResult.warnings,
            autoLeaveRequired: checkResult.autoLeaveRequired,
            processed: [...checkResult.processed, ...processedLeaves]
        };
    }
};

// Admin Dashboard API functions
export const adminApi = {
    // Get all admins from the admins table
    async getAdmins() {
        // console.log('👥 Fetching all admins from database...');
        
        const { data, error } = await supabase
            .from('admins')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) {
            console.error('❌ Error fetching admins:', error);
            throw error;
        }

        // console.log('✅ Admins fetched successfully:', data?.length || 0);
        return data || [];
    },

    // Get all employees (admins are NOT employees)
    // Get all employees AND admins combined
async getAllEmployeesAndAdmins() {
    console.log('👥 Fetching ALL users (employees + admins) from database...');
    
    try {
        // Get employees from employees table
        console.log('📋 Fetching from employees table...');
        const employees = await employeeApi.getEmployees();
        console.log('✅ Employees loaded:', employees.length);
        
        // Get admins from admins table
        console.log('📋 Fetching from admins table...');
        const admins = await this.getAdmins();
        console.log('✅ Admins loaded:', admins.length);
        
        // Add role field to employees for consistency
        const employeesWithRole = employees.map(emp => ({
            ...emp,
            role: emp.role || 'employee',
            isAdmin: false,
            source: 'employees'
        }));
        
        // Add role field to admins for consistency
        const adminsWithRole = admins.map(admin => ({
            ...admin,
            role: admin.role || 'admin',
            isAdmin: true,
            source: 'admins',
            // Map admin fields to match employee structure
            employee_id: admin.id, // Use admin id as employee_id for filtering
            // Ensure name is built from first_name/last_name if not present
            name: admin.name || `${admin.first_name || ''} ${admin.last_name || ''}`.trim(),
            first_name: admin.first_name || admin.name?.split(' ')[0] || '',
            last_name: admin.last_name || admin.name?.split(' ').slice(1).join(' ') || '',
            email: admin.email,
            phone: admin.phone || 'N/A',
            department: admin.department || 'Administration',
            position: admin.position || admin.role || 'Admin',
            join_date: admin.join_date || (admin.created_at ? admin.created_at.split('T')[0] : null), 
            status: admin.status || (admin.is_active ? 'Active' : 'Terminated')
        }));
        
        // Combine both arrays
        const allUsers = [...employeesWithRole, ...adminsWithRole];
        
        console.log('🎯 Final result:');
        console.log(`   - Total employees: ${employeesWithRole.length}`);
        console.log(`   - Total admins: ${adminsWithRole.length}`);
        console.log(`   - Total users: ${allUsers.length}`);
        
        return allUsers; // ✅ NOW RETURNING BOTH!
        
    } catch (error) {
        console.error('❌ Error fetching users:', error);
        throw error;
    }
},

    // Simple employee name fetcher
    getEmployeeName: async (userId) => {
        try {
            // console.log('🔍 Fetching employee name for user_id:', userId);
            
            const { data: employee, error } = await supabase
                .from('employees')
                .select('id, employee_id, first_name, last_name, name')
                .or(`employee_id.eq.${userId},id.eq.${userId}`)
                .single();

            if (error) {
                // console.log('❌ Error fetching employee:', error);
                return null;
            }

            const name = employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || `Employee ${userId.slice(-4)}`;
            // console.log('✅ Found employee name:', name);
            return name;
        } catch (error) {
            // console.log('❌ Exception in getEmployeeName:', error);
            return `Employee ${userId.slice(-4)}`;
        }
    },

    // Debug function to check database connectivity and data
    async debugDatabase() {
        // console.log('🔍 Debugging database connection and data...');
        
        try {
            // Test leave_requests table
            const { data: leaveData, error: leaveError, count: leaveCount } = await supabase
                .from('leave_requests')
                .select('*', { count: 'exact' })
                .limit(5);
            
            // console.log('📋 Leave requests table:', {
            //     data: leaveData,
            //     error: leaveError,
            //     count: leaveCount
            // });

            // Test employees table
            const { data: empData, error: empError, count: empCount } = await supabase
                .from('employees')
                .select('*', { count: 'exact' })
                .limit(5);
            
            // console.log('👥 Employees table:', {
            //     data: empData,
            //     error: empError,
            //     count: empCount
            // });

            // Check for potential relationships
            if (leaveData && leaveData.length > 0 && empData && empData.length > 0) {
                // console.log('🔗 Checking relationships:');
                // console.log('📝 Sample leave request employee_id:', leaveData[0].employee_id);
                // console.log('👤 Sample employee employee_id:', empData[0].employee_id);
                // console.log('👤 Sample employee id:', empData[0].id);
                // console.log('👤 Sample employee name:', empData[0].name);
                
                // Try to find matching patterns
                const employeeIdsFromRequests = leaveData.map(req => req.employee_id).filter(Boolean);
                const employeeIds = empData.map(emp => emp.employee_id).filter(Boolean);
                const empIds = empData.map(emp => emp.id).filter(Boolean);
                
                // console.log('🔍 Unique employee_ids in leave_requests:', [...new Set(employeeIdsFromRequests)]);
                // console.log('🔍 Unique employee_ids in employees:', [...new Set(employeeIds)]);
                // console.log('🔍 Unique ids in employees:', [...new Set(empIds)]);
                
                // Check for matches
                const matchByEmployeeId = employeeIdsFromRequests.some(empId => empIds.includes(empId));
                
                // console.log('🎯 Match found (employee_id -> employee.id):', matchByEmployeeId);
            }

            return {
                leaveRequests: { data: leaveData, error: leaveError, count: leaveCount },
                employees: { data: empData, error: empError, count: empCount }
            };
        } catch (error) {
            console.error('❌ Database debug error:', error);
            return { error };
        }
    },
    async getDashboardStats() {
        try {
            // console.log('📈 Getting dashboard stats for employees only...');
            
            // Get all employees from employees table (NOT admins)
            const { data: employees, error: empError } = await supabase
                .from('employees')
                .select('id, status, created_at, name, email');
            
            if (empError) {
                console.error('❌ Error fetching employees:', empError);
            }
            
            const activeEmployees = (employees || []).filter(emp => 
                !emp.status || emp.status === 'active' || emp.status === 'Active'
            );
            
            // console.log('📊 Employee Statistics:');
            console.log(`   - Total Employees: ${employees?.length || 0}`);
            console.log(`   - Active Employees: ${activeEmployees.length}`);

            // Get pending leave requests (try different status approaches)
            let { data: pendingLeaves, error: leaveError } = await supabase
                .from('leave_requests')
                .select('*')
                .eq('status', 'pending');
            
            // If no pending requests or status column doesn't exist, get all requests
            if (leaveError || !pendingLeaves || pendingLeaves.length === 0) {
                // console.log('⚠️ No pending status found, getting all leave requests...');
                const fallback = await supabase
                    .from('leave_requests')
                    .select('*');
                
                if (!fallback.error && fallback.data) {
                    pendingLeaves = fallback.data.filter(req => 
                        !req.status || req.status === 'pending' || req.status === null
                    );
                    // console.log('📋 Found leave requests (filtered):', pendingLeaves.length);
                } else {
                    console.error('❌ Error fetching all leave requests:', fallback.error);
                    pendingLeaves = [];
                }
            }
            
            if (leaveError) {
                console.error('⚠️ Leave request query error:', leaveError);
            }

            // Get approved leaves for current month
            const currentMonth = new Date().toISOString().slice(0, 7);
            const today = new Date().toISOString().slice(0, 10);
            
            const { data: approvedLeaves, error: approvedError } = await supabase
                .from('leave_requests')
                .select('*')
                .eq('status', 'approved');
            
            if (approvedError) throw approvedError;

            // Get currently active leaves (approved and ongoing today)
            const { data: activeLeaves, error: activeLeavesError } = await supabase
                .from('leave_requests')
                .select('*')
                .eq('status', 'approved')
                .lte('start_date', today)
                .gte('end_date', today);
            
            if (activeLeavesError) console.error('Error fetching active leaves:', activeLeavesError);

            // Get all leave requests for this month
            const { data: thisMonthLeaves, error: monthError } = await supabase
                .from('leave_requests')
                .select('*')
                .gte('created_at', `${currentMonth}-01`)
                .lt('created_at', `${currentMonth}-32`);
            
            if (monthError) console.error('Error fetching month leaves:', monthError);

            // Get pending timesheets (including null/undefined status and submitted)
            const { data: pendingTimesheets, error: timesheetError } = await supabase
                .from('timesheets')
                .select('*')
                .or('status.eq.pending,status.eq.submitted,status.is.null');
            
            if (timesheetError) console.error('Error fetching timesheets:', timesheetError);

            // Calculate new employees this month
            const newEmployeesThisMonth = (employees || []).filter(emp => 
                emp.created_at && emp.created_at.startsWith(currentMonth)
            ).length;

            // Calculate employees currently on leave
            const employeesOnLeave = activeLeaves?.length || 0;
            const activeEmployeesToday = Math.max(0, activeEmployees.length - employeesOnLeave);
            
            // console.log('📊 Dashboard stats calculated:', {
            //     totalEmployees: employees?.length || 0,
            //     activeEmployees: activeEmployees.length,
            //     newEmployeesThisMonth,
            //     pendingRequests: pendingLeaves.length,
            //     approvedLeaves: approvedLeaves?.length || 0,
            //     employeesOnLeave,
            //     activeEmployeesToday,
            //     pendingTimesheets: pendingTimesheets?.length || 0
            // });

            return {
                // Employee counts (admins are NOT included)
                totalEmployees: employees?.length || 0,
                employeesCount: employees?.length || 0,
                activeStaff: activeEmployees.length,
                
                // New employees this month
                newEmployeesThisMonth,
                
                // Leave statistics
                pendingRequests: pendingLeaves.length,
                approvedLeaves: approvedLeaves?.length || 0,
                employeesOnLeave,
                
                // Active today
                activeEmployeesToday,
                
                // Other metrics
                pendingTimesheets: pendingTimesheets?.length || 0,
                pendingLeaveDetails: pendingLeaves.slice(0, 5), // Latest 5 for display
                thisMonthLeaves: thisMonthLeaves?.length || 0,
            };
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            throw error;
        }
    },

    /**
     * Unified dashboard data loader for AdminDashboard
     * Fetches stats, pending leaves, and pending timesheets in parallel.
     * Ensures all pending records have employee names via server-side joins.
     */
    async loadDashboardData() {
        try {
            const today = new Date().toISOString().slice(0, 10);
            const currentMonth = new Date().toISOString().slice(0, 7);

            console.log('📊 Admin API: Loading dashboard data...');
            console.log('Today:', today);
            console.log('Current month:', currentMonth);

            // Execute all requests in parallel
            const [
                { data: employees, error: empError },
                { data: pendingLeaves, error: leaveError },
                { data: pendingTimesheets, error: tsError },
                { data: activeLeaves, error: activeLeavesError }
            ] = await Promise.all([
                // 1. All employees for stats
                supabase.from('employees').select('id, status, created_at, name, email'),
                
                // 2. Pending leaves with employee names (JOINED)
                supabase.from('leave_requests')
                    .select(`
                        *,
                        employees (
                            name,
                            email,
                            role,
                            employee_id
                        )
                    `)
                    .or('status.eq.pending,status.is.null')
                    .order('created_at', { ascending: false }),
                
                // 3. Pending timesheets with employee names (JOINED)
                supabase.from('timesheets')
                    .select(`
                        *,
                        employees (
                            name,
                            email,
                            role,
                            employee_id
                        )
                    `)
                    .or('status.eq.pending,status.eq.submitted,status.is.null')
                    .order('date', { ascending: false }),
                
                // 4. Currently active leaves for "Active Today" calculation
                supabase.from('leave_requests')
                    .select('id')
                    .eq('status', 'approved')
                    .lte('start_date', today)
                    .gte('end_date', today)
            ]);

            if (empError) {
                console.error('❌ Error fetching employees:', empError);
                throw empError;
            }
            if (leaveError) {
                console.error('❌ Error fetching pending leaves:', leaveError);
                throw leaveError;
            }
            if (tsError) {
                console.error('❌ Error fetching pending timesheets:', tsError);
                throw tsError;
            }

            console.log('✅ Employees fetched:', employees?.length);
            console.log('✅ Pending leaves fetched:', pendingLeaves?.length);
            console.log('✅ Active leaves fetched:', activeLeaves?.length);
            console.log('✅ Pending timesheets fetched:', pendingTimesheets?.length);
            
            // Log sample leave request to verify employee data
            if (pendingLeaves && pendingLeaves.length > 0) {
                console.log('📋 Sample leave request with employee data:', pendingLeaves[0]);
            }

            // Calculate counts
            const totalEmployees = employees?.length || 0;
            const activeStaffCount = (employees || []).filter(emp => 
                !emp.status || emp.status === 'active' || emp.status === 'Active'
            ).length;

            const employeesOnLeave = activeLeaves?.length || 0;
            const activeEmployeesToday = Math.max(0, activeStaffCount - employeesOnLeave);
            const newStaffThisMonth = (employees || []).filter(emp => 
                emp.created_at && emp.created_at.startsWith(currentMonth)
            ).length;

            const stats = {
                totalEmployees: totalEmployees,
                newEmployeesThisMonth: newStaffThisMonth,
                activeToday: activeEmployeesToday,
                onLeave: employeesOnLeave,
                pendingRequests: pendingLeaves?.length || 0,
                pendingTimesheets: pendingTimesheets?.length || 0,
                attendanceRate: totalEmployees > 0 ? Math.round((activeEmployeesToday / totalEmployees) * 100) : 0
            };

            console.log('✅ Calculated stats:', stats);

            return {
                stats,
                pendingLeaves: pendingLeaves || [],
                pendingTimesheets: pendingTimesheets || []
            };
        } catch (error) {
            console.error('❌ Error in loadDashboardData:', error);
            throw error;
        }
    },

    async getRecentActivity() {
        // console.log('🔍 Fetching pending leave requests with direct employee join...');
        
        const { data, error } = await supabase
            .from('leave_requests')
            .select(`
                *,
                employees (
                    id,
                    employee_id,
                    first_name,
                    last_name,
                    name,
                    email,
                    role
                )
            `)
            .or('status.eq.pending,status.is.null')
            .order('created_at', { ascending: false });

        if (error) {
            console.error('❌ Error fetching pending leaves with join:', error);
            throw error;
        }

        // console.log('✅ Fetched pending leaves with employee data:', data);
        return data;
    },

    async getPendingLeaveRequestsWithEmployeeData() {
        // console.log('🔍 Fetching pending leave requests with direct employee join...');
        
        const { data, error } = await supabase
            .from('leave_requests')
            .select(`
                *,
                employees (
                    id,
                    employee_id,
                    first_name,
                    last_name,
                    name,
                    email,
                    role
                )
            `)
            .or('status.eq.pending,status.is.null')
            .order('created_at', { ascending: false });

        if (error) {
            console.error('❌ Error fetching pending leaves with join:', error);
            throw error;
        }

        // console.log('✅ Fetched pending leaves with employee data:', data);
        return data;
    },

    async approveLeaveRequest(requestId) {
        // console.log('✅ Approving leave request:', requestId);
        
        // First, get the leave request details to find the employee
        const { data: leaveRequest, error: fetchError } = await supabase
            .from('leave_requests')
            .select('*')
            .eq('id', requestId)
            .single();
        
        if (fetchError) {
            console.error('❌ Error fetching leave request:', fetchError);
            throw fetchError;
        }
        
        // Update the leave request status to approved
        const { data, error } = await supabase
            .from('leave_requests')
            .update({ 
                status: 'approved'
            })
            .eq('id', requestId)
            .select();
        
        if (error) {
            console.error('❌ Error approving request:', error);
            throw error;
        }
        
        // Calculate the number of leave days
        const startDate = new Date(leaveRequest.start_date);
        const endDate = new Date(leaveRequest.end_date);
        const leaveDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
        
        // Update leave_balances to reflect the approved leave
        if (leaveRequest.employee_id && leaveRequest.leave_type) {
            const leaveType = leaveRequest.leave_type.toLowerCase();
            const usedColumn = leaveType === 'casual' ? 'casual_used' : 'sick_used';
            const currentYear = new Date().getFullYear();
            
            // Get current balance
            const { data: balanceData } = await supabase
                .from('leave_balances')
                .select('*')
                .eq('employee_id', leaveRequest.employee_id)
                .eq('year', currentYear)
                .maybeSingle();
            
            if (balanceData) {
                const currentUsed = balanceData[usedColumn] || 0;
                
                const { error: updateError } = await supabase
                    .from('leave_balances')
                    .update({ 
                        [usedColumn]: currentUsed + leaveDays,
                        updated_at: new Date().toISOString()
                    })
                    .eq('employee_id', leaveRequest.employee_id)
                    .eq('year', currentYear);
                
                if (updateError) {
                    console.error('⚠️ Error updating leave balance:', updateError);
                } else {
                    console.log(`✅ Leave balance updated: ${usedColumn} increased by ${leaveDays}`);
                }
            }
        }
        
        // Update employee status to "Leave"
        if (leaveRequest.employee_id) {
            const employeeIdToUpdate = leaveRequest.employee_id;
            // console.log('📝 Updating employee status to Leave for ID:', employeeIdToUpdate);
            
            // Try updating employees table first (by id)
            const { data: empUpdate1, error: empError1 } = await supabase
                .from('employees')
                .update({ status: 'Leave' })
                .eq('id', employeeIdToUpdate);
            
            if (empError1) {
                console.warn('⚠️ Failed to update employees table by id:', empError1.message);
                
                // Try updating by employee_id field if id failed
                const { data: empUpdate2, error: empError2 } = await supabase
                    .from('employees')
                    .update({ status: 'Leave' })
                    .eq('employee_id', employeeIdToUpdate);
                
                if (empError2) {
                    console.warn('⚠️ Failed to update employees table by employee_id:', empError2.message);
                } else {
                    // console.log('✅ Employee status updated to Leave (via employee_id field)');
                }
            } else {
                // console.log('✅ Employee status updated to Leave (via id field)');
            }
        } else {
            console.error('⚠️ No employee_id found in leave request, cannot update employee status');
        }
        
        // console.log('✅ Leave request approved successfully:', data[0]);
        return data[0];
    },

    async rejectLeaveRequest(requestId, reason = '') {
        // console.log('❌ Rejecting leave request:', requestId);
        
        // First, get the leave request details to find the employee
        const { data: leaveRequest, error: fetchError } = await supabase
            .from('leave_requests')
            .select('*')
            .eq('id', requestId)
            .single();
        
        if (fetchError) {
            console.error('❌ Error fetching leave request:', fetchError);
            throw fetchError;
        }
        
        // Update the leave request status to rejected
        const { data, error } = await supabase
            .from('leave_requests')
            .update({ 
                status: 'rejected'
            })
            .eq('id', requestId)
            .select();
        
        if (error) {
            console.error('❌ Error rejecting request:', error);
            throw error;
        }
        
        // Update employee status back to "Active" since leave was rejected
        if (leaveRequest.employee_id) {
            const employeeIdToUpdate = leaveRequest.employee_id;
            // console.log('📝 Updating employee status to Active for ID:', employeeIdToUpdate);
            
            // Try updating employees table first (by id)
            const { data: empUpdate1, error: empError1 } = await supabase
                .from('employees')
                .update({ status: 'Active' })
                .eq('id', employeeIdToUpdate);
            
            if (empError1) {
                console.warn('⚠️ Failed to update employees table by id:', empError1.message);
                
                // Try updating by employee_id field if id failed
                const { data: empUpdate2, error: empError2 } = await supabase
                    .from('employees')
                    .update({ status: 'Active' })
                    .eq('employee_id', employeeIdToUpdate);
                
                if (empError2) {
                    console.warn('⚠️ Failed to update employees table by employee_id:', empError2.message);
                } else {
                    // console.log('✅ Employee status updated to Active (via employee_id field)');
                }
            } else {
                // console.log('✅ Employee status updated to Active (via id field)');
            }
        } else {
            console.error('⚠️ No employee_id found in leave request, cannot update employee status');
        }
        
        // console.log('✅ Leave request rejected successfully:', data[0]);
        return data[0];
    },

    // Function to update employee status when leave ends
    async updateEmployeeStatusAfterLeave(userId) {
        // console.log('🔄 Checking if employee should return to active status:', userId);
        
        const today = new Date().toISOString().slice(0, 10);
        
        // Check if employee has any active leaves today
        const { data: activeLeaves, error } = await supabase
            .from('leave_requests')
            .select('*')
            .eq('status', 'approved')
            .lte('start_date', today)
            .gte('end_date', today)
            .eq('employee_id', userId);
        
        if (error) {
            console.error('❌ Error checking active leaves:', error);
            return;
        }
        
        // If no active leaves, set status back to Active
        if (!activeLeaves || activeLeaves.length === 0) {
            // console.log('📝 No active leaves found, setting employee status to Active');
            
            // Try updating by employee_id first
            const { error: empError1 } = await supabase
                .from('employees')
                .update({ status: 'Active' })
                .eq('employee_id', userId);
            
            if (empError1) {
                // If that fails, try updating by id
                const { error: empError2 } = await supabase
                    .from('employees')
                    .update({ status: 'Active' })
                    .eq('id', userId);
                
                if (empError2) {
                    console.error('❌ Failed to update employee status:', empError2);
                } else {
                    // console.log('✅ Employee status updated to Active (by id)');
                }
            } else {
                // console.log('✅ Employee status updated to Active (by employee_id)');
            }
        } else {
            // console.log('📅 Employee still has active leaves, keeping On-leave status');
        }
    }
};

// Helper function to calculate number of leave days
function calculateLeaveDays(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Include both start and end days
    return diffDays;
}

// Account Details API functions
export const accountDetailsApi = {
    // Get account details for an employee
    async getAccountDetails(employeeId) {
        console.log('📋 Getting account details for employee:', employeeId);
        
        const { data, error } = await supabase
            .from('account_details')
            .select('*')
            .eq('employee_id', employeeId)
            .maybeSingle();
        
        if (error) {
            console.error('❌ Error fetching account details:', error);
            throw error;
        }
        
        return data;
    },

    // Save (insert or update) account details
    async saveAccountDetails(employeeId, details) {
        console.log('💾 Saving account details for employee:', employeeId);
        
        // Check if record exists
        const existing = await this.getAccountDetails(employeeId);
        
        const accountData = {
            employee_id: employeeId,
            bank_name: details.bank_name || null,
            bank_account_number: details.bank_account_number || null,
            ifsc_code: details.ifsc_code || null,
            aadhaar_number: details.aadhaar_number || null,
            aadhaar_address: details.aadhaar_address || null,
            pan_number: details.pan_number || null,
            updated_at: new Date().toISOString()
        };
        
        let result;
        
        if (existing) {
            // Update existing record
            const { data, error } = await supabase
                .from('account_details')
                .update(accountData)
                .eq('employee_id', employeeId)
                .select();
            
            if (error) throw error;
            result = data[0];
        } else {
            // Insert new record
            accountData.created_at = new Date().toISOString();
            
            const { data, error } = await supabase
                .from('account_details')
                .insert([accountData])
                .select();
            
            if (error) throw error;
            result = data[0];
        }
        
        console.log('✅ Account details saved successfully');
        return result;
    },

    // Get all account details (for admin)
    async getAllAccountDetails() {
        console.log('📋 Getting all account details (admin)');
        
        const { data, error } = await supabase
            .from('account_details')
            .select(`
                *,
                employees (
                    name,
                    email,
                    phone,
                    employee_id,
                    department,
                    position
                )
            `)
            .order('updated_at', { ascending: false });
        
        if (error) {
            console.error('❌ Error fetching all account details:', error);
            throw error;
        }
        
        return data || [];
    },

    // Get account details by employee ID (for admin modal)
    async getAccountDetailsByEmployeeId(employeeId) {
        console.log('📋 Getting account details for employee (admin view):', employeeId);
        
        // Get account details
        const { data: accountData, error: accError } = await supabase
            .from('account_details')
            .select('*')
            .eq('employee_id', employeeId)
            .maybeSingle();
        
        if (accError) {
            console.error('❌ Error fetching account details:', accError);
        }
        
        // Get employee info
        const { data: employeeData, error: empError } = await supabase
            .from('employees')
            .select('name, email, phone, employee_id, department, position')
            .eq('employee_id', employeeId)
            .maybeSingle();
        
        if (empError) {
            console.error('❌ Error fetching employee info:', empError);
        }
        
        return {
            account: accountData || {},
            employee: employeeData || {}
        };
    }
};

// ============================================
// EMS STORAGE API - For file uploads to EMS_bucket
// ============================================
export const emsStorageApi = {
    // Upload file to EMS_bucket with employee authentication
    async uploadFile(file, folder = 'general', employeeId = null) {
        try {
            if (!file) {
                throw new Error('No file provided');
            }

            // Get current user session for authentication
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
            
            if (sessionError) {
                console.error('❌ Session error:', sessionError);
                throw new Error('Authentication required to upload files');
            }

            // Generate organized file path with employee ID and timestamp
            const fileExt = file.name.split('.').pop();
            const timestamp = Date.now();
            const randomString = Math.random().toString(36).substring(7);
            const userPrefix = employeeId ? `emp-${employeeId}` : 'user';
            const fileName = `${folder}/${userPrefix}/${timestamp}-${randomString}.${fileExt}`;

            console.log('📤 Uploading file to EMS_bucket:', {
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                destination: fileName,
                authenticated: !!session
            });

            // Upload file to EMS_bucket with proper headers
            const { data, error } = await supabase.storage
                .from('EMS_bucket')
                .upload(fileName, file, {
                    cacheControl: '3600',
                    upsert: false,
                    contentType: file.type
                });

            if (error) {
                console.error('❌ Error uploading file:', error);
                // Check for specific RLS error
                if (error.message?.includes('row-level security') || error.message?.includes('violates')) {
                    throw new Error('Permission denied: Please ensure you are logged in and have upload permissions');
                }
                throw error;
            }

            // Get public URL for the uploaded file
            const { data: { publicUrl } } = supabase.storage
                .from('EMS_bucket')
                .getPublicUrl(fileName);

            console.log('✅ File uploaded successfully:', publicUrl);

            return {
                path: data.path,
                publicUrl: publicUrl,
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                uploadedBy: employeeId,
                uploadedAt: new Date().toISOString()
            };
        } catch (error) {
            console.error('❌ File upload failed:', error);
            throw new Error(`Failed to upload file: ${error.message}`);
        }
    },

    // Delete file from EMS_bucket
    async deleteFile(filePath) {
        try {
            const { error } = await supabase.storage
                .from('EMS_bucket')
                .remove([filePath]);

            if (error) {
                console.error('❌ Error deleting file:', error);
                throw error;
            }

            console.log('✅ File deleted successfully:', filePath);
            return true;
        } catch (error) {
            console.error('❌ File deletion failed:', error);
            throw new Error(`Failed to delete file: ${error.message}`);
        }
    }
};

// ============================================
// REIMBURSEMENT REQUESTS API
// ============================================
export const reimbursementApi = {
    // Create a new reimbursement request with file upload
    async createRequest(requestData, receiptFile = null) {
        console.log('💰 Creating reimbursement request:', requestData);
        
        let receiptUrl = null;
        let receiptPath = null;
        
        // Upload receipt file if provided
        if (receiptFile) {
            try {
                const uploadResult = await emsStorageApi.uploadFile(
                    receiptFile, 
                    'reimbursements/receipts',
                    requestData.employee_id
                );
                receiptUrl = uploadResult.publicUrl;
                receiptPath = uploadResult.path;
            } catch (uploadError) {
                console.error('❌ Failed to upload receipt:', uploadError);
                throw new Error('Failed to upload receipt file');
            }
        }
        
        const { data, error } = await supabase
            .from('reimbursement_requests')
            .insert([{
                employee_id: requestData.employee_id,
                category: requestData.category,
                description: requestData.description,
                amount: requestData.amount,
                date: requestData.date,
                status: 'pending',
                receipt_name: receiptFile?.name || requestData.receipt_name,
                receipt_url: receiptUrl,
                receipt_path: receiptPath,
                receipt_type: receiptFile?.type || requestData.receipt_type,
                receipt_size: receiptFile?.size || requestData.receipt_size
            }])
            .select()
            .single();
        
        if (error) {
            // Try to delete uploaded file if database insert fails
            if (receiptPath) {
                try {
                    await emsStorageApi.deleteFile(receiptPath);
                } catch (deleteError) {
                    console.error('⚠️ Failed to cleanup uploaded file:', deleteError);
                }
            }
            console.error('❌ Error creating reimbursement request:', error);
            throw error;
        }
        
        console.log('✅ Reimbursement request created:', data);
        return data;
    },

    // Get all reimbursement requests for an employee
    async getRequestsByEmployee(employeeId) {
        console.log('📋 Getting reimbursement requests for employee:', employeeId);
        
        const { data, error } = await supabase
            .from('reimbursement_requests')
            .select('*')
            .eq('employee_id', employeeId)
            .order('created_at', { ascending: false });
        
        if (error) {
            console.error('❌ Error fetching reimbursement requests:', error);
            throw error;
        }
        
        return data || [];
    },

    // Get reimbursement statistics for an employee
    async getReimbursementStats(employeeId) {
        console.log('📊 Getting reimbursement stats for employee:', employeeId);
        
        const { data, error } = await supabase
            .from('reimbursement_requests')
            .select('status, amount')
            .eq('employee_id', employeeId);
        
        if (error) {
            console.error('❌ Error fetching reimbursement stats:', error);
            throw error;
        }
        
        const stats = {
            pending: 0,
            approved: 0,
            rejected: 0
        };
        
        (data || []).forEach(req => {
            if (stats[req.status] !== undefined) {
                stats[req.status] += parseFloat(req.amount);
            }
        });
        
        return stats;
    },

    // Update reimbursement request status (admin only)
    async updateStatus(requestId, status) {
        console.log('🔄 Updating reimbursement request status:', requestId, status);
        
        const { data, error } = await supabase
            .from('reimbursement_requests')
            .update({ status })
            .eq('id', requestId)
            .select()
            .single();
        
        if (error) {
            console.error('❌ Error updating reimbursement status:', error);
            throw error;
        }
        
        return data;
    },

    // Update an existing reimbursement request (for employees to edit their pending requests)
    async updateRequest(requestId, requestData, receiptFile = null) {
        console.log('✏️ Updating reimbursement request:', requestId, requestData);
        
        let receiptUrl = requestData.receipt_url;
        let receiptPath = requestData.receipt_path;
        let receiptName = requestData.receipt_name;
        let receiptType = requestData.receipt_type;
        let receiptSize = requestData.receipt_size;
        
        // Upload new receipt file if provided
        if (receiptFile) {
            try {
                // Delete old receipt file if exists
                if (receiptPath) {
                    try {
                        await emsStorageApi.deleteFile(receiptPath);
                        console.log('🗑️ Deleted old receipt file:', receiptPath);
                    } catch (deleteError) {
                        console.warn('⚠️ Failed to delete old receipt file:', deleteError);
                    }
                }
                
                const uploadResult = await emsStorageApi.uploadFile(
                    receiptFile, 
                    'reimbursements/receipts',
                    requestData.employee_id
                );
                receiptUrl = uploadResult.publicUrl;
                receiptPath = uploadResult.path;
                receiptName = receiptFile.name;
                receiptType = receiptFile.type;
                receiptSize = receiptFile.size;
            } catch (uploadError) {
                console.error('❌ Failed to upload new receipt:', uploadError);
                throw new Error('Failed to upload receipt file');
            }
        }
        
        const { data, error } = await supabase
            .from('reimbursement_requests')
            .update({
                category: requestData.category,
                description: requestData.description,
                amount: requestData.amount,
                date: requestData.date,
                receipt_name: receiptName,
                receipt_url: receiptUrl,
                receipt_path: receiptPath,
                receipt_type: receiptType,
                receipt_size: receiptSize,
                updated_at: new Date().toISOString()
            })
            .eq('id', requestId)
            .select()
            .single();
        
        if (error) {
            console.error('❌ Error updating reimbursement request:', error);
            throw error;
        }
        
        console.log('✅ Reimbursement request updated:', data);
        return data;
    },

    // Delete a reimbursement request
    async deleteRequest(requestId) {
        console.log('🗑️ Deleting reimbursement request:', requestId);
        
        const { error } = await supabase
            .from('reimbursement_requests')
            .delete()
            .eq('id', requestId);
        
        if (error) {
            console.error('❌ Error deleting reimbursement request:', error);
            throw error;
        }
        
        return true;
    },

    // Get all reimbursement requests (for admin)
    async getAllRequests() {
        console.log('📋 Getting all reimbursement requests (admin)');
        
        const { data, error } = await supabase
            .from('reimbursement_requests')
            .select(`
                *,
                employees:employee_id (
                    name,
                    email,
                    employee_id
                )
            `)
            .order('created_at', { ascending: false });
        
        if (error) {
            console.error('❌ Error fetching all reimbursement requests:', error);
            throw error;
        }
        
        return data || [];
    }
};

// ============================================
// INVENTORY ITEMS API
// ============================================
export const inventoryApi = {
    // Create a new inventory item with image uploads
    async createItem(itemData, itemImageFile = null, invoiceImageFile = null) {
        console.log('📦 Creating inventory item:', itemData);
        
        let itemImageUrl = null;
        let itemImagePath = null;
        let invoiceImageUrl = null;
        let invoiceImagePath = null;
        const uploadedFiles = [];
        
        // Upload item image if provided
        if (itemImageFile) {
            try {
                const uploadResult = await emsStorageApi.uploadFile(
                    itemImageFile, 
                    'inventory/item-images',
                    itemData.employee_id
                );
                itemImageUrl = uploadResult.publicUrl;
                itemImagePath = uploadResult.path;
                uploadedFiles.push(itemImagePath);
            } catch (uploadError) {
                console.error('❌ Failed to upload item image:', uploadError);
                throw new Error('Failed to upload item image');
            }
        }
        
        // Upload invoice image if provided
        if (invoiceImageFile) {
            try {
                const uploadResult = await emsStorageApi.uploadFile(
                    invoiceImageFile, 
                    'inventory/invoice-images',
                    itemData.employee_id
                );
                invoiceImageUrl = uploadResult.publicUrl;
                invoiceImagePath = uploadResult.path;
                uploadedFiles.push(invoiceImagePath);
            } catch (uploadError) {
                console.error('❌ Failed to upload invoice image:', uploadError);
                // Cleanup already uploaded files
                for (const filePath of uploadedFiles) {
                    try {
                        await emsStorageApi.deleteFile(filePath);
                    } catch (deleteError) {
                        console.error('⚠️ Failed to cleanup uploaded file:', deleteError);
                    }
                }
                throw new Error('Failed to upload invoice image');
            }
        }
        
        const { data, error } = await supabase
            .from('inventory_items')
            .insert([{
                employee_id: itemData.employee_id,
                item_name: itemData.item_name,
                item_details: itemData.item_details,
                category: itemData.category,
                serial_number: itemData.serial_number,
                condition: itemData.condition || 'new',
                status: 'assigned',
                item_image_name: itemImageFile?.name || itemData.item_image_name,
                item_image_url: itemImageUrl,
                item_image_path: itemImagePath,
                item_image_type: itemImageFile?.type || itemData.item_image_type,
                item_image_size: itemImageFile?.size || itemData.item_image_size,
                invoice_image_name: invoiceImageFile?.name || itemData.invoice_image_name,
                invoice_image_url: invoiceImageUrl,
                invoice_image_path: invoiceImagePath,
                invoice_image_type: invoiceImageFile?.type || itemData.invoice_image_type,
                invoice_image_size: invoiceImageFile?.size || itemData.invoice_image_size,
                added_by: itemData.added_by || 'Employee'
            }])
            .select(`
                *,
                employees:employee_id (
                    name,
                    email,
                    employee_id
                )
            `)
            .single();
        
        if (error) {
            // Cleanup uploaded files if database insert fails
            for (const filePath of uploadedFiles) {
                try {
                    await emsStorageApi.deleteFile(filePath);
                } catch (deleteError) {
                    console.error('⚠️ Failed to cleanup uploaded file:', deleteError);
                }
            }
            console.error('❌ Error creating inventory item:', error);
            throw error;
        }
        
        console.log('✅ Inventory item created:', data);
        return data;
    },

    // Get all inventory items for an employee
    async getItemsByEmployee(employeeId) {
        console.log('📋 Getting inventory items for employee:', employeeId);
        
        const { data, error } = await supabase
            .from('inventory_items')
            .select('*')
            .eq('employee_id', employeeId)
            .order('created_at', { ascending: false });
        
        if (error) {
            console.error('❌ Error fetching inventory items:', error);
            throw error;
        }
        
        return data || [];
    },

    // Get inventory statistics for an employee
    async getInventoryStats(employeeId) {
        console.log('📊 Getting inventory stats for employee:', employeeId);
        
        const { data, error } = await supabase
            .from('inventory_items')
            .select('status')
            .eq('employee_id', employeeId);
        
        if (error) {
            console.error('❌ Error fetching inventory stats:', error);
            throw error;
        }
        
        const stats = {
            assigned: 0,
            pending: 0,
            returned: 0
        };
        
        (data || []).forEach(item => {
            if (stats[item.status] !== undefined) {
                stats[item.status]++;
            }
        });
        
        return stats;
    },

    // Update inventory item status
    async updateStatus(itemId, status) {
        console.log('🔄 Updating inventory item status:', itemId, status);
        
        const { data, error } = await supabase
            .from('inventory_items')
            .update({ status })
            .eq('id', itemId)
            .select()
            .single();
        
        if (error) {
            console.error('❌ Error updating inventory status:', error);
            throw error;
        }
        
        return data;
    },

    // Delete an inventory item
    async deleteItem(itemId) {
        console.log('🗑️ Deleting inventory item:', itemId);
        
        const { error } = await supabase
            .from('inventory_items')
            .delete()
            .eq('id', itemId);
        
        if (error) {
            console.error('❌ Error deleting inventory item:', error);
            throw error;
        }
        
        return true;
    },

    // Get all inventory items (for admin)
    async getAllItems() {
        console.log('📋 Getting all inventory items (admin)');
        
        const { data, error } = await supabase
            .from('inventory_items')
            .select(`
                *,
                employees:employee_id (
                    name,
                    email,
                    employee_id
                )
            `)
            .order('created_at', { ascending: false });
        
        if (error) {
            console.error('❌ Error fetching all inventory items:', error);
            throw error;
        }
        
        return data || [];
    }
};

